<template>
  <main>
    <div id="ntabiPayResult">
      <section class="container">
        <div class="result-card" :class="{ 'is-cxl': cancelDone }">

          <!-- 확인중 -->
          <div v-if="loading" class="state">
            <div class="spinner-border text-success" role="status"></div>
            <h4>결제를 확인하고 있습니다</h4>
            <p class="msg">창을 닫거나 새로고침하지 마세요.</p>
          </div>

          <!-- 성공 (취소 후에도 영수증은 그대로 보여줌) -->
          <div v-else-if="ok" class="state">
            <template v-if="!cancelDone">
              <i class="bi bi-check-circle-fill icon ok"></i>
              <h4>결제가 완료되었습니다</h4>
            </template>
            <template v-else>
              <i class="bi bi-arrow-counterclockwise icon cxl"></i>
              <h4>결제가 취소되었습니다</h4>
              <p class="msg">카드사 사정에 따라 승인취소 반영까지 시간이 걸릴 수 있습니다.</p>
            </template>

            <table class="receipt">
              <tbody>
                <tr><th>결제번호</th><td>{{ pay.payNo }}</td></tr>
                <tr><th>결제타이틀</th><td>{{ pay.payTitle }}</td></tr>
                <tr v-if="pay.lines && pay.lines.length">
                  <th>상품/수량</th>
                  <td>
                    <div v-for="(ln, i) in pay.lines" :key="i">
                      {{ ln.salePdt }} × {{ ln.qty }} = {{ getCurrencyFormat(ln.lineAmt) }}원
                    </div>
                  </td>
                </tr>
                <tr v-if="pay.payMethod"><th>결제수단</th><td>{{ pay.payMethod }}</td></tr>
                <tr v-if="pay.payerName"><th>결제자</th><td>{{ pay.payerName }}</td></tr>
                <tr v-if="cancelDone"><th>취소사유</th><td>{{ cancelReason }}</td></tr>
                <tr class="total">
                  <th>{{ cancelDone ? '취소금액' : '결제금액' }}</th>
                  <td>{{ getCurrencyFormat(pay.payAmt) }}원</td>
                </tr>
              </tbody>
            </table>

            <button v-if="!cancelDone" class="btn btn-cxl" :disabled="canceling" @click="cancelPay">
              {{ canceling ? '취소 처리중...' : '전액 결제취소' }}
            </button>
          </div>

          <!-- 실패 -->
          <div v-else class="state">
            <i class="bi bi-x-circle-fill icon fail"></i>
            <h4>{{ canceled ? '결제가 취소되었습니다' : '결제에 실패했습니다' }}</h4>
            <p class="msg">{{ message }}</p>
            <p v-if="payNo" class="pay-no">결제번호 {{ payNo }}</p>
          </div>

          <div v-if="!loading" class="btn-row">
            <button class="btn sub" :disabled="canceling" @click="goList">결제내역</button>
            <button class="btn go" :disabled="canceling" @click="goNewPay">새 결제하기</button>
          </div>
        </div>
      </section>
    </div>
  </main>
</template>

<script>
export default {
  props: {
    mode: { type: String, default: "success" },   // success | fail
  },

  data() {
    return {
      loading: true,
      ok: false,
      canceled: false,        // 결제창 단계에서 사용자가 취소
      message: "",
      payNo: "",
      pay: {},

      canceling: false,       // 결제취소 처리중
      cancelDone: false,      // 결제완료 후 취소 완료
      cancelReason: "",
    };
  },

  mounted() {
    const userEl = document.getElementById("user");
    if (userEl) userEl.style.display = "none";
    document.querySelectorAll(".nav-item").forEach(el => (el.style.display = "none"));

    if (this.mode === "success") this.confirmPay();
    else this.failPay();
  },

  beforeUnmount() {
    document.querySelectorAll(".nav-item").forEach(el => (el.style.display = ""));
  },

  methods: {
    // 토스 successUrl: ?paymentKey=&orderId=&amount=
    async confirmPay() {
      const { paymentKey, orderId, amount } = this.$route.query;
      this.payNo = orderId || "";

      if (!paymentKey || !orderId || !amount) {
        this.showFail("잘못된 접근입니다.");
        return;
      }

      // 금액 검증과 토스 승인은 전부 서버에서 처리 (새로고침해도 서버가 중복승인을 막음)
      const res = await this.$api("/api/ntabiTossPay/confirm", {
        param: [{ paymentKey, orderId, amount: Number(amount) }],
      });

      // $api는 실패해도 throw하지 않으므로 ok 필드로만 성공 판단
      if (res && res.ok === true && res.pay) {
        this.pay = res.pay;
        this.ok = true;
      } else {
        this.showFail(res?.message || "결제 승인 확인에 실패했습니다. 결제번호를 관리자에게 알려주세요.");
      }
      this.loading = false;
    },

    // 토스 failUrl: ?code=&message=&orderId=
    async failPay() {
      const { code, message, orderId } = this.$route.query;
      this.payNo = orderId || "";
      this.canceled = code === "PAY_PROCESS_CANCELED" || code === "USER_CANCEL";

      if (orderId) {
        await this.$api("/api/ntabiTossPay/fail", {
          param: [{ orderId, code: code || "FAIL", message: message || "" }],
        });
      }

      this.showFail(this.canceled ? "결제창에서 결제를 취소했습니다." : (message || "결제 처리 중 오류가 발생했습니다."));
      this.loading = false;
    },

    // 결제완료 건 전액취소
    async cancelPay() {
      if (this.canceling || !this.pay.payNo) return;

      const result = await this.$swal.fire({
        icon: "warning",
        title: "결제취소",
        html: `<b>${this.getCurrencyFormat(this.pay.payAmt)}원</b> 결제를 전액 취소합니다.<br>취소 후에는 되돌릴 수 없습니다.`,
        input: "text",
        inputPlaceholder: "취소사유 (필수)",
        inputAttributes: { maxlength: 200 },
        inputValidator: v => (!v || !v.trim() ? "취소사유를 입력해주세요." : undefined),
        showCancelButton: true,
        confirmButtonText: "결제취소",
        cancelButtonText: "닫기",
        confirmButtonColor: "#e74c3c",
      });
      if (!result.isConfirmed) return;

      const reason = result.value.trim();
      this.canceling = true;
      const res = await this.$api("/api/ntabiTossPay/cancel", {
        param: [{ payNo: this.pay.payNo, cancelReason: reason }],
      });
      this.canceling = false;

      if (res && res.ok === true) {
        this.cancelReason = reason;
        this.cancelDone = true;
        this.$swal.fire("", "결제가 취소되었습니다.", "success");
      } else {
        this.$swal.fire("", res?.message || "결제취소에 실패했습니다. 결제내역에서 상태를 확인해주세요.", "error");
      }
    },

    showFail(msg) {
      this.ok = false;
      this.message = msg;
      this.loading = false;
    },

    goNewPay() {
      this.$router.replace({ path: "/ma_TossPay" });
    },

    goList() {
      this.$router.push({ path: "/ma_NtabiTossPayList" });
    },

    getCurrencyFormat(value) {
      return this.$currencyFormat(value);
    },
  },
};
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Noto+Sans+KR:wght@400;500;600;700&display=swap');

#ntabiPayResult {
  --ci-green:  #86cd53;
  --charcoal:  #1c1c1c;
  --text-sub:  #5a5a5a;
  --gray-50:   #fafaf8;
  --gray-100:  #f5f5f5;
  --gray-200:  #e8e8e8;
  --gray-400:  #aaaaaa;
  --red:       #e74c3c;

  background: var(--gray-50);
  font-family: 'Noto Sans KR', sans-serif;
  padding: 4rem 0;
  min-height: 70vh;
}

.result-card {
  max-width: 520px;
  margin: 0 auto;
  background: #fff;
  border: 1px solid var(--gray-200);
  border-radius: 14px;
  box-shadow: 0 4px 20px rgba(0,0,0,0.08);
  padding: 2.4rem 1.6rem 2rem;
  text-align: center;
  border-top: 5px solid var(--ci-green);
}
.result-card.is-cxl { border-top-color: var(--gray-400); }

.state h4 {
  font-weight: 700;
  margin: 1rem 0 0.6rem;
}
.icon { font-size: 3.4rem; }
.icon.ok   { color: var(--ci-green); }
.icon.fail { color: var(--red); }
.icon.cxl  { color: var(--gray-400); }

.msg {
  color: var(--text-sub);
  font-size: 0.9rem;
  margin-bottom: 0.4rem;
}
.pay-no {
  color: var(--gray-400);
  font-size: 0.8rem;
}

.receipt {
  width: 100%;
  margin: 1.4rem 0 0.4rem;
  font-size: 0.88rem;
  text-align: left;
  border-top: 1px dashed var(--gray-200);
}
.receipt th,
.receipt td {
  padding: 0.6rem 0.2rem;
  border-bottom: 1px dashed var(--gray-200);
  vertical-align: top;
}
.receipt th {
  width: 90px;
  color: var(--text-sub);
  font-weight: 600;
}
.receipt .total th,
.receipt .total td {
  font-size: 1.05rem;
  font-weight: 800;
  border-bottom: none;
}
.receipt .total td { color: var(--ci-green); }
.is-cxl .receipt .total td {
  color: var(--gray-400);
  text-decoration: line-through;
}

.btn-cxl {
  margin-top: 1rem;
  width: 100%;
  background: #fff;
  color: var(--red);
  border: 1.5px solid var(--red);
  border-radius: 8px;
  font-weight: 700;
  padding: 11px;
}
.btn-cxl:hover:not(:disabled) { background: #fdf0ee; color: var(--red); }

.btn-row {
  display: flex;
  gap: 0.6rem;
  margin-top: 1.6rem;
}
.btn-row .btn {
  flex: 1;
  border-radius: 8px;
  font-weight: 700;
  padding: 13px;
}
.sub {
  background: var(--gray-100);
  color: var(--text-sub);
  border: 1.5px solid var(--gray-200);
}
.sub:hover:not(:disabled) { background: var(--gray-200); }
.go {
  background: var(--charcoal);
  color: #fff;
  border: none;
}
.go:hover:not(:disabled) { background: #333; color: #fff; }
.btn:disabled { opacity: 0.5; cursor: not-allowed; }
</style>